import { FormLabel, styled, Typography } from '@mui/material'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import Icon from '@mui/material/Icon'
import React from 'react'
import { GoogleReCaptcha } from 'react-google-recaptcha-v3'
import { z } from 'zod'

import { Form } from '@/app/component/form'
import SubmitButton from '@/app/component/form/submit-button'
import { useSignupEmail } from '@/app/module/auth/hooks/use-signup-email'

type SignupEmailInfo = { email: string }

const SignupEmailForm: React.FC = () => {
  const [success, setSuccess] = React.useState(false)
  const [verifyRecaptcha, setVerifyRecaptcha] = React.useState(false)
  const [email, setEmail] = React.useState('') // we need to store the email to pass it to the signupEmail function

  const [signupEmail, { isLoading }] = useSignupEmail()

  const handleRecaptchaVerify = async (verifyToken: string) => {
    setVerifyRecaptcha(false)
    setSuccess(await signupEmail({ email, verifyToken }))
  }

  const handleFormSubmit = (data: SignupEmailInfo) => {
    setEmail(data.email)
    setVerifyRecaptcha(true)
  }

  if (success) {
    return (
      <StyledCard>
        <StyledCardContent>
          <StyledIcon>email_circle</StyledIcon>
          <Typography align="center" sx={{ marginTop: 2, marginBottom: 2 }}>
            Thank you! We have sent you an email. <br />
            Please verify your email address by clicking on the link inside.
          </Typography>
        </StyledCardContent>
      </StyledCard>
    )
  }

  return (
    <>
      <StyledCard>
        <StyledCardContent>
          <StyledIcon>person</StyledIcon>
          <FormLabel sx={{ marginTop: 2, marginBottom: 2 }}>Please enter your email to sign up.</FormLabel>
          <Form
            defaultValues={{ email: '' }}
            fields={signupEmailFields}
            schema={signupEmailSchema}
            onSubmit={handleFormSubmit}
            renderActions={({ isDirty }) => (
              <StyledActions>
                <SubmitButton isDirty={isDirty} isSubmitting={isLoading}>
                  Sign up
                </SubmitButton>
              </StyledActions>
            )}
          />
        </StyledCardContent>
      </StyledCard>
      {verifyRecaptcha && <GoogleReCaptcha onVerify={handleRecaptchaVerify} />}
    </>
  )
}

const signupEmailFields = {
  email: { fieldProps: { label: 'Email', name: 'email', type: 'email', required: true }, type: 'text' },
} as const

const signupEmailSchema = z.object({
  email: z.string().min(1, { message: 'Email is required' }).email({ message: 'Invalid email address' }),
})

const StyledCard = styled(Card)({
  maxWidth: '400px',
  margin: 'auto',
  marginTop: `calc(60px + 5%)`,
  marginBottom: '3rem',
})

const StyledCardContent = styled(CardContent)({ display: 'flex', flexDirection: 'column', paddingTop: '2rem' })

const StyledIcon = styled(Icon)(({ theme }) => ({
  margin: 'auto',
  fontSize: '4rem !important',
  color: theme.palette.primary.main,
}))

const StyledActions = styled(CardActions)({ justifyContent: 'flex-end', padding: 0 })

export default SignupEmailForm
